/* ResumeDraftDialog — iter101.75
 *
 * Premium "welcome back" modal shown when a doctor opens an in-flight
 * draft case from My Cases. Companion to CaseSubmittedDialog (submit
 * celebration) and LeaveWizardDialog (leave confirmation) — same hero
 * DNA (tinted gradient, animated rotating ring, sparkle scatter,
 * itinerary, dual CTAs) so the three moments of the wizard's life
 * cycle feel like a family.
 *
 * Why does this exist?
 * ---------------------
 * The old resume flow was a silent toast ("Draft resumed") that a
 * distracted doctor would blink past. As a result, doctors reported
 * feeling like they were starting from Step 1 on every resume — even
 * when the wizard had auto-forwarded them to Step 3, 4 or 5 — because
 * nothing on the page said "here's what you already did".
 *
 * ResumeDraftDialog fixes this by showing a hero card with:
 *   - Big "Picking up where you left off" eyebrow.
 *   - Doctor-friendly title + case-code chip.
 *   - Body that names the exact step the doctor lands on next.
 *   - A checklist itinerary of WHAT HAS BEEN DONE so far (patient
 *     picked, planning mode chosen, scans uploaded, objectives
 *     added, etc.) — filled ticks live from `progress` prop.
 *   - Dual CTAs: primary "Continue from Step X" · secondary "Start
 *     from Step 1" (for the rare doctor who wants to re-check earlier
 *     steps).
 *
 * Contract:
 *   <ResumeDraftDialog
 *       open={showResume}
 *       caseCode={draftCase.code}
 *       resumeStep={4}
 *       stepLabel="Treatment Plan"
 *       doctorName={scannerProfile.name}
 *       progress={[
 *           { key: "patient",  label: "Patient picked",       done: true,  detail: "Ravi Sharma" },
 *           { key: "planning", label: "Planning mode chosen", done: true,  detail: "Invisique™ Team" },
 *           { key: "scans",    label: "Scans + photos",       done: false, detail: "0 of 8 uploaded" },
 *           …
 *       ]}
 *       onContinue={() => setOpen(false)}         // stays on current step
 *       onStartOver={() => { setStep(1); setOpen(false); }}
 *   />
 */
import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
    ClockCounterClockwise,
    ArrowRight,
    Sparkle,
    CheckCircle,
    Circle,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

/* Tone: teal (family with LeaveWizardDialog / CaseSubmittedDialog). */
const TONE = {
    heroBg:      "bg-gradient-to-br from-teal-100/70 via-teal-50/40 to-transparent",
    eyebrowBg:   "bg-teal-100 text-teal-800",
    iconBg:      "bg-teal-500 text-white shadow-[0_8px_28px_rgba(20,184,166,0.35)]",
    ring:        "border-teal-500/40",
    ctaBg:       "bg-teal-600 hover:bg-teal-700",
    doneIconBg:  "bg-emerald-100 text-emerald-700",
    pendingIconBg:"bg-muted/60 text-muted-foreground/60",
    sparkleColor:"bg-teal-400",
};

export default function ResumeDraftDialog({
    open,
    caseCode = "",
    resumeStep = 1,
    stepLabel = "Case Setup",
    doctorName = "",
    progress = [],
    onContinue,
}) {
    const doneCount = progress.filter((p) => p.done).length;
    const totalCount = progress.length || 1;
    const firstName = (doctorName || "").split(" ").filter(Boolean).slice(-1)[0]
        || (doctorName || "").split(" ").filter(Boolean)[0]
        || "";
    const greeting = firstName ? `Welcome back, Dr. ${firstName}` : "Welcome back";

    return (
        <Dialog
            open={open}
            // Overlay/ESC = "continue on current step" (never strands the doctor)
            onOpenChange={(o) => { if (!o) onContinue?.(); }}
        >
            <DialogContent
                className="max-w-md p-0 gap-0 border-0 shadow-2xl overflow-hidden"
                data-testid="resume-draft-dialog"
            >
                {/* Hero */}
                <div className={`relative px-6 pt-6 pb-4 overflow-hidden ${TONE.heroBg}`}>
                    <Decorations sparkleClass={TONE.sparkleColor} />

                    <AnimatePresence>
                        {open && (
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 240, damping: 16, delay: 0.06 }}
                                className={`relative mx-auto w-16 h-16 rounded-full grid place-items-center ${TONE.iconBg}`}
                                data-testid="resume-draft-icon"
                            >
                                <motion.span
                                    aria-hidden
                                    className={`absolute inset-[-5px] rounded-full border-2 ${TONE.ring}`}
                                    initial={{ rotate: 0, opacity: 0 }}
                                    animate={{ rotate: 360, opacity: 1 }}
                                    transition={{
                                        rotate: { duration: 14, repeat: Infinity, ease: "linear" },
                                        opacity: { duration: 0.4 },
                                    }}
                                />
                                <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.22, type: "spring", stiffness: 320 }}
                                >
                                    <ClockCounterClockwise size={28} weight="fill" />
                                </motion.span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className="text-center mt-3.5">
                        <div
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] ${TONE.eyebrowBg}`}
                            data-testid="resume-draft-eyebrow"
                        >
                            <Sparkle size={10} weight="fill" /> Picking up where you left off
                        </div>
                        <h2
                            className="font-display text-xl font-bold tracking-tight mt-2 leading-tight"
                            data-testid="resume-draft-title"
                        >
                            {greeting}
                        </h2>
                        {caseCode && (
                            <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white border border-border font-mono text-[11.5px] font-semibold text-foreground shadow-sm">
                                {caseCode}
                            </div>
                        )}
                        <p className="text-[12.5px] text-muted-foreground mt-2.5 leading-relaxed max-w-sm mx-auto">
                            Your progress is safe — everything you&apos;ve captured so far is loaded and ready. We&apos;ll drop you on <b className="text-foreground">Step {resumeStep} · {stepLabel}</b>, right where you paused.
                        </p>
                    </div>
                </div>

                {/* Progress checklist */}
                <div className="px-6 py-4 bg-card">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                            What you&apos;ve done so far
                        </div>
                        <div
                            className="text-[10.5px] font-bold tabular-nums text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full ring-1 ring-emerald-200"
                            data-testid="resume-draft-progress-count"
                        >
                            {doneCount} / {totalCount}
                        </div>
                    </div>
                    <ol className="space-y-3 relative">
                        {progress.map((p, idx) => (
                            <li
                                key={p.key || idx}
                                className="flex items-start gap-3"
                                data-testid={`resume-draft-step-${p.key || idx}`}
                            >
                                <motion.span
                                    initial={{ opacity: 0, x: -8 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.3 + idx * 0.06 }}
                                    className={`shrink-0 w-7 h-7 rounded-full grid place-items-center ${p.done ? TONE.doneIconBg : TONE.pendingIconBg}`}
                                >
                                    {p.done
                                        ? <CheckCircle size={14} weight="fill" />
                                        : <Circle size={12} weight="regular" />}
                                </motion.span>
                                <motion.div
                                    initial={{ opacity: 0, x: -4 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.32 + idx * 0.06 }}
                                    className="flex-1 min-w-0 pt-0.5"
                                >
                                    <div className={`text-[13px] font-semibold leading-tight ${p.done ? "text-foreground" : "text-muted-foreground/70"}`}>
                                        {p.label}
                                    </div>
                                    {p.detail && (
                                        <div className={`text-[11px] mt-0.5 leading-snug ${p.done ? "text-muted-foreground" : "text-muted-foreground/60"}`}>
                                            {p.detail}
                                        </div>
                                    )}
                                </motion.div>
                            </li>
                        ))}
                    </ol>
                </div>

                {/* CTAs */}
                <div className="px-6 pb-4 pt-2 bg-card flex items-center gap-2 flex-wrap border-t border-border/50">
                    <Button
                        onClick={onContinue}
                        size="sm"
                        className={`w-full justify-center rounded-full h-9 text-white gap-1.5 ${TONE.ctaBg}`}
                        data-testid="resume-draft-continue-btn"
                    >
                        Continue on Step {resumeStep} <ArrowRight size={12} weight="bold" />
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Decorations({ sparkleClass = "bg-primary" }) {
    return (
        <>
            <span className={`absolute top-3 left-4 w-1.5 h-1.5 rounded-full opacity-30 animate-ping [animation-duration:2200ms] [animation-delay:200ms] ${sparkleClass}`} />
            <span className={`absolute top-7 right-7 w-1 h-1 rounded-full opacity-40 animate-ping [animation-duration:2400ms] [animation-delay:500ms] ${sparkleClass}`} />
            <span className={`absolute bottom-4 left-10 w-1 h-1 rounded-full opacity-40 animate-ping [animation-duration:2600ms] [animation-delay:800ms] ${sparkleClass}`} />
            <span className={`absolute bottom-7 right-10 w-1.5 h-1.5 rounded-full opacity-25 animate-ping [animation-duration:2000ms] [animation-delay:0ms] ${sparkleClass}`} />
        </>
    );
}
